import { useState } from 'react';
import { request } from '../lib/api';

type BackupFile = { name: string; bytes: number; sha256: string };
type WritableFile = { write(chunk: Uint8Array | string): Promise<void>; close(): Promise<void>; abort(): Promise<void> };
type Folder = { getFileHandle(name: string, options: {create: boolean}): Promise<{createWritable(): Promise<WritableFile>}> };

export function LaptopBackup({ files, stamp }: { files: BackupFile[]; stamp: string }) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  async function save() {
    const picker = (window as unknown as {showDirectoryPicker?: (options: {mode: string}) => Promise<Folder>}).showDirectoryPicker;
    if (!picker) { setMessage('Folder selection needs Chrome or Edge on desktop. Use the individual Download buttons in this browser.'); return; }
    setBusy(true);
    let output: WritableFile | undefined;
    try {
      const folder = await picker({mode: 'readwrite'});
      for (const file of files) {
        const ticket = await request<{url: string}>('/api/backups/download', {method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify({name:file.name})});
        const response = await fetch(ticket.url);
        if (!response.ok || !response.body) throw new Error('Backup download failed. No complete backup was saved.');
        output = await (await folder.getFileHandle(file.name, {create:true})).createWritable();
        const reader = response.body.getReader();
        let received = 0;
        try {
          for (;;) {
            const {done,value} = await reader.read();
            if (done) break;
            await output.write(value);
            received += value.byteLength;
            setMessage(`Saving ${file.name}: ${Math.min(100, Math.floor(received / file.bytes * 100))}%`);
          }
          if (received !== file.bytes) throw new Error('Download interrupted. Retry to save a complete backup.');
          await output.close(); output = undefined;
        } finally { await reader.cancel().catch(()=>undefined); }
      }
      output = await (await folder.getFileHandle(`backup-${stamp}.json`, {create:true})).createWritable();
      await output.write(JSON.stringify({version:1,stamp,files,savedAt:new Date().toISOString()},null,2));
      await output.close(); output = undefined;
      setMessage('Backup saved to the selected folder, including its checksum manifest. Keep the folder private.');
    } catch (error) {
      await output?.abort().catch(()=>undefined);
      setMessage(error instanceof DOMException && error.name === 'AbortError' ? 'Folder selection cancelled.' : error instanceof Error ? error.message : 'Backup could not be saved.');
    } finally {setBusy(false);}
  }
  return <div><button type="button" disabled={busy} onClick={()=>void save()}>{busy ? 'Saving to laptop…' : 'Save backup to laptop folder'}</button>{message && <p role="status">{message}</p>}</div>;
}
